import type { AssistantProjectContextSnapshot } from '@/features/assistant/tools/project-tools'
import { getCurrentProject } from '@/features/assistant/tools/project-tools'
import type { AssistantProjectMediaSnapshot } from '@/features/assistant/tools/media-tools'
import { listProjectMedia } from '@/features/assistant/tools/media-tools'
import type { AssistantRuntimeCapabilities } from '@/features/assistant/tools/runtime-tools'
import { getRuntimeCapabilities } from '@/features/assistant/tools/runtime-tools'
import type { AuthMembership } from '@/features/auth/types'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'
import { useEditorStore } from '@/app/state/editor/store'
import { getWorkspaceRoot } from '@/infrastructure/storage/workspace-fs/root'
import { useProjectStore } from '@/features/projects/stores/project-store'
import { useMediaLibraryStore } from '@/features/media-library/stores/media-library-store'
import { ASSISTANT_TOOL_ERROR_CODES, AssistantToolError } from '@/features/assistant/tool-errors'

export interface AssistantToolSamplingClient {
  createMessage: (params: {
    messages: Array<{
      role: 'user' | 'assistant' | 'system'
      content: Array<{
        type: 'text'
        text: string
      }>
    }>
    systemPrompt?: string
    temperature?: number
    maxTokens?: number
  }) => Promise<{
    role: 'assistant'
    content: Array<{
      type: 'text'
      text: string
    }>
  }>
}

export interface AssistantToolContext {
  readonly invocationSource: 'app' | 'mcp'
  readonly currentProjectId: string | null
  readonly currentWorkspaceName: string | null
  readonly currentEditorWorkspace: string | null
  readonly isUserSignedIn: boolean
  readonly subscriptionTier: string | null
  readonly accessSummary: AuthMembership | null
  readonly samplingClient?: AssistantToolSamplingClient
  ensureWorkspaceReady: () => Promise<{ workspaceName: string }>
  ensureCurrentProject: () => AssistantProjectContextSnapshot
  getCurrentProjectSnapshot: () => AssistantProjectContextSnapshot | null
  getRuntimeCapabilities: () => AssistantRuntimeCapabilities
  refreshProjectState: () => Promise<AssistantProjectContextSnapshot | null>
  refreshMediaItems: (projectId?: string) => Promise<AssistantProjectMediaSnapshot>
}

export interface CreateAssistantToolContextOptions {
  invocationSource?: 'app' | 'mcp'
  samplingClient?: AssistantToolSamplingClient
}

async function ensureWorkspacePermission(
  handle: FileSystemDirectoryHandle,
): Promise<{ workspaceName: string }> {
  if (typeof handle.queryPermission !== 'function') {
    return { workspaceName: handle.name }
  }

  try {
    const permission = await handle.queryPermission({ mode: 'readwrite' })
    if (permission === 'denied') {
      throw new AssistantToolError(
        ASSISTANT_TOOL_ERROR_CODES.workspacePermissionDenied,
        'Workspace permission was denied.',
        {
          details: { workspaceName: handle.name },
        },
      )
    }
  } catch (error) {
    if (error instanceof AssistantToolError) {
      throw error
    }

    throw new AssistantToolError(
      ASSISTANT_TOOL_ERROR_CODES.workspacePermissionDenied,
      'Unable to verify workspace permission.',
      {
        cause: error,
        details: { workspaceName: handle.name },
      },
    )
  }

  return { workspaceName: handle.name }
}

function getReadyWorkspaceRoot(): FileSystemDirectoryHandle {
  const root = getWorkspaceRoot()
  if (!root) {
    throw new AssistantToolError(
      ASSISTANT_TOOL_ERROR_CODES.workspaceNotReady,
      'Workspace is not ready.',
    )
  }

  return root
}

export function createAssistantToolContext(
  options: CreateAssistantToolContextOptions = {},
): AssistantToolContext {
  const invocationSource = options.invocationSource ?? 'app'

  return {
    invocationSource,
    samplingClient: options.samplingClient,
    get currentProjectId() {
      return useProjectStore.getState().currentProject?.id ?? null
    },
    get currentWorkspaceName() {
      return getWorkspaceRoot()?.name ?? null
    },
    get currentEditorWorkspace() {
      return useEditorStore.getState().workspace ?? null
    },
    get isUserSignedIn() {
      return useAuthSessionStore.getState().isAuthenticated()
    },
    get subscriptionTier() {
      return useAuthSessionStore.getState().session?.membership?.planCode ?? null
    },
    get accessSummary() {
      return useAuthSessionStore.getState().session?.membership ?? null
    },
    async ensureWorkspaceReady() {
      const root = getReadyWorkspaceRoot()

      // The local MCP bridge runs inside the same browser session that already
      // passed WorkspaceGate. Re-querying File System Access permission here
      // has proven brittle on real pages and can stall otherwise valid MCP
      // calls. Storage operations still enforce permission at the actual FS
      // boundary, so returning the active workspace root is safe.
      if (invocationSource === 'mcp') {
        return { workspaceName: root.name }
      }

      return ensureWorkspacePermission(root)
    },
    ensureCurrentProject() {
      try {
        return getCurrentProject()
      } catch (error) {
        throw new AssistantToolError(
          ASSISTANT_TOOL_ERROR_CODES.noCurrentProject,
          'No current project is open.',
          {
            cause: error,
          },
        )
      }
    },
    getCurrentProjectSnapshot() {
      try {
        return getCurrentProject()
      } catch {
        return null
      }
    },
    getRuntimeCapabilities,
    async refreshProjectState() {
      const project = useProjectStore.getState().currentProject
      if (!project) {
        return null
      }

      const mediaStore = useMediaLibraryStore.getState()
      if (mediaStore.currentProjectId !== project.id) {
        mediaStore.setCurrentProject(project.id)
      }

      return getCurrentProject()
    },
    async refreshMediaItems(projectId) {
      return listProjectMedia(projectId, { refresh: true })
    },
  }
}
